import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { Table, Button, Icon } from "semantic-ui-react";
import { toast, ToastContainer } from "react-toastify";
import { LoadingSpinner, BasicModal, ConfirmDialog } from "../../../Shared";
import { Topic } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useLanguage } from "../../../../contexts";
import { TopicForm } from "../TopicForm/TopicForm";
import searchSvg from "../../../../assets/svg/search.svg";
import "./ListTopics.scss";

const topicController = new Topic();

export function ListTopics({ active: activeFilter, reload, onReload }) {
  const { accessToken } = useAuth();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const [topics,       setTopics]       = useState(null);
  const [filtered,     setFiltered]     = useState([]);
  const [search,       setSearch]       = useState("");
  const [showModal,    setShowModal]    = useState(false);
  const [selected,     setSelected]     = useState(null);
  const [showConfirm,  setShowConfirm]  = useState(false);
  const [isDelete,     setIsDelete]     = useState(false);
  const [rowMenu,      setRowMenu]      = useState(null); // { id, top, left, openUp } | null

  useEffect(() => {
    if (!rowMenu) return;
    const handler = (e) => {
      if (!e.target.closest(".topics-list__row-menu, .topics-list__row-menu-list")) setRowMenu(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [rowMenu]);

  const toggleRowMenu = (id, e) => {
    if (rowMenu?.id === id) { setRowMenu(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const menuHeight = 150;
    const openUp = rect.bottom + menuHeight > window.innerHeight;
    setRowMenu({
      id,
      left: rect.right - 170,
      top: openUp ? rect.top - 4 : rect.bottom + 4,
      openUp,
    });
  };

  useEffect(() => {
    (async () => {
      try {
        setTopics(null);
        const res = await topicController.getTopics(accessToken, activeFilter);
        setTopics(res);
        setFiltered(res);
      } catch {
        setTopics([]);
        setFiltered([]);
      }
    })();
  }, [activeFilter, reload]);

  useEffect(() => {
    if (!topics) return;
    if (!search) { setFiltered(topics); return; }
    const q = search.toLowerCase();
    setFiltered(topics.filter((item) =>
      item.name?.toLowerCase().includes(q) ||
      item.description?.toLowerCase().includes(q)
    ));
  }, [search, topics]);

  const openCreate = () => { setSelected(null); setShowModal(true); };
  const openEdit   = (item) => { setSelected(item); setShowModal(true); };

  const openToggle = (item) => { setSelected(item); setIsDelete(false); setShowConfirm(true); };
  const openDelete = (item) => { setSelected(item); setIsDelete(true);  setShowConfirm(true); };

  const handleToggle = async () => {
    try {
      await topicController.updateTopic(accessToken, selected._id, { active: !selected.active });
      toast.success(selected.active ? t("deactivated") : t("activated"));
      setShowConfirm(false);
      onReload();
    } catch { toast.error(t("error_updating")); }
  };

  const handleDelete = async () => {
    try {
      await topicController.deleteTopic(accessToken, selected._id);
      toast.success(t("deleted"));
      setShowConfirm(false);
      onReload();
    } catch (err) { toast.error(err?.msg || t("error_deleting")); }
  };

  if (topics === null) return <LoadingSpinner />;

  return (
    <div className="topics-list">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="topics-list__card">
        <div className="topics-list__filters">
          <div className="search-user-wrapper">
            <img src={searchSvg} alt="search" className="search-user-icon" />
            <input
              className="search-user-input"
              placeholder={`${t("search")}...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Button icon className="add-topic-btn" title={t("new_topic")} onClick={openCreate}>
            <Icon name="plus" />
          </Button>
        </div>

        {filtered.length === 0 ? (
          <div className="list-empty-state">
                <Icon name="folder open outline" className="list-empty-state__icon" />
                <p className="list-empty-state__text">{t("no_results")}</p>
              </div>
        ) : (
          <div className="topics-list__tbl-scroll">
            <Table className="topics-table unstackable rtable-cards row-hover">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>{t("name")}</Table.HeaderCell>
                  <Table.HeaderCell>{t("description")}</Table.HeaderCell>
                  <Table.HeaderCell textAlign="right"></Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {filtered.map((item) => (
                  <Table.Row key={item._id}>
                    <Table.Cell data-label={t("name")}>{item.name}</Table.Cell>
                    <Table.Cell data-label={t("description")} style={{ color: item.description ? "inherit" : "#aaa" }}>
                      {item.description || "—"}
                    </Table.Cell>
                    <Table.Cell textAlign="center" data-label="" className="topics-table__actions" onClick={(e) => e.stopPropagation()}>
                      <div className="topics-list__row-menu">
                        <button type="button" className="topics-list__row-menu-btn" onClick={(e) => toggleRowMenu(item._id, e)} aria-label={t("actions")}>
                          <Icon name="ellipsis vertical" />
                        </button>
                      </div>
                      {rowMenu?.id === item._id && ReactDOM.createPortal(
                        <div
                          className="topics-list__row-menu-list"
                          style={{
                            position: "fixed",
                            top: rowMenu.top,
                            left: rowMenu.left,
                            transform: rowMenu.openUp ? "translateY(-100%)" : "none",
                          }}
                        >
                          <button onClick={() => { openEdit(item); setRowMenu(null); }}>
                            <Icon name="pencil" />{t("edit")}
                          </button>
                          <button onClick={() => { openToggle(item); setRowMenu(null); }}>
                            <Icon name={item.active ? "ban" : "check"} />{item.active ? t("deactivate") : t("activate")}
                          </button>
                          <button className="topics-list__row-menu-item--danger" onClick={() => { openDelete(item); setRowMenu(null); }}>
                            <Icon name="trash" />{t("delete")}
                          </button>
                        </div>,
                        document.body
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}
      </div>

      <BasicModal
        show={showModal}
        close={() => setShowModal(false)}
        title={selected ? `${t("edit")}: ${selected.name}` : t("new_topic")}
        size="small"
        className="catalog-modal"
      >
        <TopicForm topic={selected} close={() => setShowModal(false)} onReload={onReload} />
      </BasicModal>

      <ConfirmDialog
        open={showConfirm}
        title={isDelete ? t("delete") : (selected?.active ? t("deactivate") : t("activate"))}
        message={isDelete
          ? `¿${t("delete")} "${selected?.name}"?`
          : `¿${selected?.active ? t("deactivate") : t("activate")} "${selected?.name}"?`}
        onCancel={() => setShowConfirm(false)}
        onConfirm={isDelete ? handleDelete : handleToggle}
        confirmLabel={t("accept")}
        cancelLabel={t("cancel")}
        confirmDanger={isDelete}
      />
    </div>
  );
}
